/**
 * Consent-Aware Analytics Initialization
 * Handles initialization of analytics scripts only after user consent
 * Supports GA4, PostHog, and other tracking services
 */

import { hasConsent, onConsentChange, getConsent } from "./cookieConsent";

// Track initialization state
let ga4Initialized = false;
let posthogInitialized = false;

// Configuration
const GA4_MEASUREMENT_ID = "G-EJKVDG3H9W";
const POSTHOG_API_KEY = "phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE";
const POSTHOG_API_HOST = "https://us.i.posthog.com";

/**
 * Initialize Google Analytics 4 with consent mode
 */
export function initGA4() {
  if (ga4Initialized || typeof window === "undefined") return;

  try {
    // Check if GA4 is already loaded from HTML head
    const isAlreadyLoaded =
      typeof window.gtag === "function" && window.dataLayer?.length > 0;

    if (!isAlreadyLoaded) {
      // Create gtag function if not already present
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== "function") {
        function gtag() {
          window.dataLayer.push(arguments);
        }
        window.gtag = gtag;
      }

      // Initialize with timestamp (only if not already done)
      gtag("js", new Date());

      // Load the GA4 script if not already loaded
      const existingScript = document.querySelector(
        `script[src*="googletagmanager.com/gtag/js"]`,
      );
      if (!existingScript) {
        const script = document.createElement("script");
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
        document.head.appendChild(script);
      }
    } else {
      console.log("[Analytics] GA4 already loaded from HTML head");
      // Consent mode should already be set in HTML head, but verify
      // Don't override if already set
    }

    // Mark as initialized regardless of how it was loaded
    ga4Initialized = true;

    // Only set default consent if not already set (check dataLayer)
    if (typeof window.gtag === "function") {
      // Check if consent was already set in HTML head
      const consentAlreadySet = window.dataLayer?.some(
        (item) => item[0] === "consent" && item[1] === "default",
      );

      if (!consentAlreadySet) {
        // Set default consent state (denied until user consents)
        window.gtag("consent", "default", {
          analytics_storage: "denied",
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
          wait_for_update: 500,
        });
      }
    }

    console.log("[Analytics] GA4 initialized with consent mode");

    // Update consent based on current preferences
    updateGA4Consent();
  } catch (error) {
    console.error("[Analytics] Error initializing GA4:", error);
  }
}

/**
 * Update GA4 consent based on current preferences
 */
export function updateGA4Consent() {
  // Allow updating consent even if not initialized by our code
  // (e.g., if GA4 was loaded from HTML head)
  if (typeof window === "undefined" || !window.gtag) {
    console.warn("[Analytics] Cannot update GA4 consent - gtag not available");
    return;
  }

  const consent = getConsent();
  if (!consent) {
    // No consent given yet, keep defaults (denied)
    return;
  }

  try {
    const consentState = {
      analytics_storage: consent.analytics ? "granted" : "denied",
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
    };

    window.gtag("consent", "update", consentState);

    console.log("[Analytics] GA4 consent updated:", {
      analytics: consent.analytics,
      marketing: consent.marketing,
    });

    // Verify consent was set by checking dataLayer
    if (process.env.NODE_ENV === "development") {
      setTimeout(() => {
        const consentInDataLayer = window.dataLayer?.find(
          (item) =>
            Array.isArray(item) &&
            item[0] === "consent" &&
            item[1] === "update",
        );
        if (consentInDataLayer) {
          console.log(
            "[Analytics] Consent verified in dataLayer:",
            consentInDataLayer[2],
          );
        }
      }, 100);
    }
  } catch (error) {
    console.error("[Analytics] Error updating GA4 consent:", error);
  }
}

/**
 * Initialize PostHog with consent check
 */
export function initPostHog() {
  if (posthogInitialized || typeof window === "undefined") return;

  // Check if PostHog is already initialized (from previous session or elsewhere)
  if (typeof window.posthog !== "undefined" && window.posthog !== null) {
    posthogInitialized = true;
    console.log("[Analytics] PostHog already initialized, skipping");
    return;
  }

  // Only initialize if analytics consent is granted
  if (!hasConsent("analytics")) {
    console.log(
      "[Analytics] PostHog initialization deferred - awaiting consent",
    );
    return;
  }

  try {
    // Dynamically import PostHog
    import("posthog-js")
      .then((module) => {
        const posthog = module.default;

        // Check again if PostHog was initialized while we were loading
        if (typeof window.posthog !== "undefined" && window.posthog !== null) {
          posthogInitialized = true;
          console.log("[Analytics] PostHog already initialized, skipping");
          return;
        }

        posthog.init(POSTHOG_API_KEY, {
          api_host: POSTHOG_API_HOST,
          person_profiles: "identified_only",
          persistence: "localStorage",
          autocapture: hasConsent("analytics"),
          capture_pageview: hasConsent("analytics"),
          disable_session_recording: !hasConsent("analytics"),
        });

        window.posthog = posthog;
        posthogInitialized = true;

        console.log("[Analytics] PostHog initialized");
      })
      .catch((error) => {
        console.error("[Analytics] Error loading PostHog:", error);
      });
  } catch (error) {
    console.error("[Analytics] Error initializing PostHog:", error);
  }
}

/**
 * Opt out of PostHog tracking
 */
export function optOutPostHog() {
  if (typeof window !== "undefined" && window.posthog) {
    try {
      window.posthog.opt_out_capturing();
      console.log("[Analytics] PostHog opted out");
    } catch (error) {
      console.error("[Analytics] Error opting out PostHog:", error);
    }
  }
}

/**
 * Opt in to PostHog tracking
 */
export function optInPostHog() {
  if (typeof window !== "undefined" && window.posthog) {
    try {
      window.posthog.opt_in_capturing();
      console.log("[Analytics] PostHog opted in");
    } catch (error) {
      console.error("[Analytics] Error opting in PostHog:", error);
    }
  }
}

/**
 * Initialize all analytics based on current consent
 */
export function initializeAnalytics() {
  if (typeof window === "undefined") return;

  // Always initialize GA4 (with consent mode)
  initGA4();

  // Check current consent
  const consent = getConsent();

  if (consent) {
    // Update GA4 consent state
    updateGA4Consent();

    // Initialize PostHog if analytics consent is granted
    if (consent.analytics) {
      initPostHog();
    }
  }

  // Listen for consent changes
  onConsentChange((updatedConsent) => {
    console.log("[Analytics] Consent changed:", updatedConsent);

    // Update GA4 consent
    updateGA4Consent();

    // Handle PostHog based on analytics consent
    if (updatedConsent.analytics) {
      if (!posthogInitialized) {
        initPostHog();
      } else {
        optInPostHog();
      }
    } else {
      optOutPostHog();
    }

    // Reload page if marketing consent changed (for third-party pixels)
    // This ensures pixels like Facebook Pixel are properly loaded/unloaded
    if (consent && consent.marketing !== updatedConsent.marketing) {
      console.log(
        "[Analytics] Marketing consent changed - page may need reload for full effect",
      );
    }
  });
}

/**
 * Check if analytics tracking is allowed
 */
export function canTrack() {
  return hasConsent("analytics");
}

/**
 * Check if marketing tracking is allowed
 */
export function canTrackMarketing() {
  return hasConsent("marketing");
}

/**
 * Consent-aware wrapper for analytics events
 * Use this to ensure events are only sent with proper consent
 */
export function trackWithConsent(category, trackingFunction, ...args) {
  if (!hasConsent(category)) {
    console.log(`[Analytics] Tracking blocked - no consent for ${category}`);
    return;
  }

  try {
    trackingFunction(...args);
  } catch (error) {
    console.error("[Analytics] Error tracking event:", error);
  }
}
